// frontend/src/pages/CategoryPage.js
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { knowledgeAPI, categoryAPI } from "../services/api";
import KnowledgeCard from "../components/knowledge/KnowledgeCard";
import Pagination from "../components/common/Pagination";

const CategoryPage = () => {
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  });

  // Fetch category info
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const response = await categoryAPI.getById(id);
        setCategory(response.data.data);
      } catch (error) {
        console.error("Failed to fetch category:", error);
      }
    };
    fetchCategory();
  }, [id]);

  // Fetch knowledge in this category
  useEffect(() => {
    const fetchKnowledge = async () => {
      setLoading(true);
      try {
        const response = await knowledgeAPI.getByCategory(id, {
          page: pagination.page,
          limit: pagination.limit,
        });
        setKnowledge(response.data.data);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error("Failed to fetch knowledge:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledge();
  }, [id, pagination.page, pagination.limit]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLimitChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      limit: parseInt(e.target.value),
      page: 1,
    }));
  };

  if (loading && !category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {category?.catkm_name
            ? `${category.catkm_name} | หมวดหมู่องค์ความรู้`
            : "หมวดหมู่องค์ความรู้ | มหาวิทยาลัยราชภัฏศรีสะเกษ"}
        </title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="container-custom">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-8 flex-wrap">
            <Link to="/" className="text-primary-500 hover:text-primary-600">
              หน้าแรก
            </Link>
            <i className="fas fa-chevron-right text-xs"></i>
            <Link
              to="/knowledge"
              className="text-primary-500 hover:text-primary-600"
            >
              คลังปัญญา
            </Link>
            <i className="fas fa-chevron-right text-xs"></i>
            <span className="text-gray-800 font-medium">
              {category?.catkm_name || "หมวดหมู่"}
            </span>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            {category && (
              <>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-full mb-4">
                  <i
                    className={`${category.catkm_icon} text-3xl text-primary-500`}
                  ></i>
                </div>
                <h1 className="text-4xl font-bold text-primary-500 mb-2">
                  {category.catkm_name}
                </h1>
                <p className="text-gray-600">
                  องค์ความรู้ทั้งหมดในหมวดหมู่{category.catkm_name}
                </p>
              </>
            )}
          </div>

          {/* Results Info */}
          {!loading && knowledge.length > 0 && (
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-600">
                พบทั้งหมด{" "}
                <span className="font-semibold text-primary-500">
                  {pagination.total}
                </span>{" "}
                รายการ
              </p>
              <select
                value={pagination.limit}
                onChange={handleLimitChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              >
                <option value="12">12 รายการ/หน้า</option>
                <option value="24">24 รายการ/หน้า</option>
                <option value="36">36 รายการ/หน้า</option>
              </select>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <div className="spinner"></div>
            </div>
          ) : knowledge.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                {knowledge.map((item) => (
                  <KnowledgeCard key={item.knowledge_id} knowledge={item} />
                ))}
              </div>

              {pagination.totalPages > 1 && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          ) : (
            <div className="text-center py-16 bg-white rounded-lg">
              <i className="fas fa-folder-open text-5xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                ไม่พบองค์ความรู้
              </h3>
              <p className="text-gray-500">ยังไม่มีองค์ความรู้ในหมวดหมู่นี้</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
